import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface AuthCardProps {
  children: ReactNode;
  className?: string;
  maxWidth?: string;
}

const AuthCard = ({
  children,
  className,
  maxWidth = "max-w-md",
}: AuthCardProps) => {
  return (
    <div className={cn("w-full", maxWidth, className)}>{children}</div>
  );
};

interface AuthCardContainerProps {
  children: ReactNode;
  className?: string;
}

/** Single shadcn Card wrapping header + body (use CardHeader / CardContent inside). */
const AuthCardContainer = ({ children, className }: AuthCardContainerProps) => {
  return <Card className={cn("shadow-sm", className)}>{children}</Card>;
};

interface AuthCardHeaderProps {
  children: ReactNode;
  className?: string;
}

const AuthCardHeader = ({ children, className }: AuthCardHeaderProps) => {
  return (
    <CardHeader className={cn("space-y-4 text-center pb-2", className)}>
      {children}
    </CardHeader>
  );
};

interface AuthCardLogoProps {
  src: string;
  alt: string;
  className?: string;
}

const AuthCardLogo = ({ src, alt, className }: AuthCardLogoProps) => {
  return (
    <div className="flex justify-center">
      <img className={cn("h-14 w-auto", className)} src={src} alt={alt} />
    </div>
  );
};

interface AuthCardTitleProps {
  children: ReactNode;
  className?: string;
}

const AuthCardTitle = ({ children, className }: AuthCardTitleProps) => {
  return (
    <CardTitle className={cn("text-xl font-semibold", className)}>
      {children}
    </CardTitle>
  );
};

interface AuthCardDescriptionProps {
  children: ReactNode;
  className?: string;
}

const AuthCardDescription = ({
  children,
  className,
}: AuthCardDescriptionProps) => {
  return <CardDescription className={cn(className)}>{children}</CardDescription>;
};

interface AuthCardDividerProps {
  text: string;
  className?: string;
}

const AuthCardDivider = ({ text, className }: AuthCardDividerProps) => {
  return (
    <div className={cn("relative my-6 text-center", className)}>
      <div className="absolute inset-0 flex items-center" aria-hidden>
        <span className="w-full border-t border-border" />
      </div>
      <span className="relative bg-card px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {text}
      </span>
    </div>
  );
};

/** Muted feature row for optional use in asides or secondary areas */
const AuthCardFeature = ({
  icon,
  children,
  className,
}: {
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-foreground",
        className
      )}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
};

const AuthCardFeatures = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn("flex flex-wrap justify-center gap-3", className)}>
      {children}
    </div>
  );
};

export {
  AuthCard,
  AuthCardContainer,
  AuthCardHeader,
  AuthCardLogo,
  AuthCardTitle,
  AuthCardDescription,
  AuthCardDivider,
  AuthCardFeature,
  AuthCardFeatures,
  CardContent,
};
